import { getPublicClient } from '../../../../../api/companyRecords';
import { patchStorefrontAppointmentCalendar } from '../../../../../api/storefront/calendar-sync/calendarSync';
import { googleConfig } from './utils/calendarConfig';
import { parseAppointmentDate } from './utils/parseAppointmentDate';

function resolveCompanyId(appointment, companyId) {
  return companyId || appointment?.companyId || localStorage.getItem('clientCompanyId');
}

async function persistGoogleEventId(appointment, companyId, eventId) {
  if (!appointment?.id || !companyId || !eventId) return;
  const now = new Date().toISOString();
  try {
    await patchStorefrontAppointmentCalendar({
      companyId,
      appointmentId: appointment.id,
      patch: {
        googleEventId: eventId,
        lastSyncedAt: now,
        googleLastSyncedAt: now,
      },
    });
  } catch (updateError) {
    console.error('Failed to update appointment with Google event ID:', updateError);
  }
}

export const syncWithGoogleCalendar = async (appointment, userId, skipAuth = false, companyId = null) => {
  try {
    companyId = resolveCompanyId(appointment, companyId);
    if (!companyId) {
      throw new Error('Company ID is required for calendar sync');
    }

    const clientData = await getPublicClient({ companyId, id: userId });
    if (!clientData?.calendarIntegrations?.google) {
      throw new Error('Google Calendar integration is not enabled');
    }

    if (typeof window.gapi === 'undefined') {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google API client'));
        document.head.appendChild(script);
      });
    }

    if (typeof window.google?.accounts?.oauth2 === 'undefined') {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
      });
    }

    if (!window.gapi.client) {
      await new Promise((resolve, reject) => {
        window.gapi.load('client', {
          callback: resolve,
          onerror: () => reject(new Error('Failed to load GAPI client')),
        });
      });

      try {
        await window.gapi.client.init({
          apiKey: googleConfig.apiKey,
          discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
        });
      } catch (error) {
        console.error('Failed to initialize GAPI client:', error);
        throw new Error('Failed to initialize Google Calendar API');
      }
    }

    const token = localStorage.getItem('googleCalendarToken');
    const tokenExpiry = localStorage.getItem('googleCalendarTokenExpiry');
    const isTokenValid = token && tokenExpiry && new Date(tokenExpiry) > new Date();

    if (!isTokenValid) {
      await new Promise((resolve, reject) => {
        if (!window.google?.accounts?.oauth2) {
          reject(new Error('Google Identity Services not loaded'));
          return;
        }

        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: googleConfig.clientId,
          scope: googleConfig.scopes.join(' '),
          callback: (response) => {
            if (response.error) {
              reject(new Error(response.error));
            } else if (response.access_token) {
              localStorage.setItem('googleCalendarToken', response.access_token);
              localStorage.setItem(
                'googleCalendarTokenExpiry',
                new Date(Date.now() + response.expires_in * 1000).toISOString()
              );
              window.gapi.client.setToken(response);
              resolve();
            } else {
              reject(new Error('No access token received'));
            }
          },
        });

        tokenClient.requestAccessToken({ prompt: skipAuth ? '' : 'consent' });
      });
    } else {
      window.gapi.client.setToken({ access_token: token });
    }

    const startDate = parseAppointmentDate(appointment.dateTime);
    const parsedEnd = parseAppointmentDate(appointment.endTime);
    const endDate =
      parsedEnd || (startDate ? new Date(startDate.getTime() + 60 * 60 * 1000) : null);

    if (!startDate || !endDate) {
      throw new Error('Invalid appointment dates');
    }

    const event = {
      summary: `Appointment with ${appointment.clientName}`,
      description: appointment.notes || '',
      start: {
        dateTime: startDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      reminders: {
        useDefault: true,
      },
      transparency: 'opaque',
      visibility: 'private',
    };

    let response;
    if (appointment.isUpdate && appointment.googleEventId) {
      response = await window.gapi.client.calendar.events.patch({
        calendarId: 'primary',
        eventId: appointment.googleEventId,
        resource: event,
      });
    } else {
      response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
      });
    }

    if (!response?.result?.id) {
      throw new Error('No event ID received from Google Calendar');
    }

    await persistGoogleEventId(appointment, companyId, response.result.id);

    return {
      success: true,
      eventId: response.result.id,
      wasUpdated: appointment.isUpdate && appointment.googleEventId,
    };
  } catch (error) {
    console.error('Google Calendar sync error:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync with Google Calendar',
    };
  }
};

export const syncExistingAppointments = async (appointments, userId, companyId = null) => {
  try {
    const resolvedCompanyId = companyId || localStorage.getItem('clientCompanyId');
    if (!resolvedCompanyId) {
      throw new Error('Company ID is required for calendar sync');
    }

    const clientData = await getPublicClient({ companyId: resolvedCompanyId, id: userId });
    if (!clientData?.calendarIntegrations?.google) {
      throw new Error('Google Calendar integration is not enabled');
    }

    const results = [];

    for (const appointment of appointments) {
      if (appointment.googleEventId) continue;

      const result = await syncWithGoogleCalendar(
        {
          ...appointment,
          companyId: appointment.companyId || resolvedCompanyId,
          isUpdate: false,
        },
        userId,
        false,
        resolvedCompanyId
      );

      results.push({
        appointmentId: appointment.id,
        ...result,
      });
    }

    return {
      success: true,
      results,
    };
  } catch (error) {
    console.error('Error syncing existing appointments:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};
